import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Resizable } from 're-resizable';
import localforage from 'localforage';
import * as d3 from "d3";
import * as fc from "d3fc";
import {
  useRecoilState,
  useSetRecoilState,
  useRecoilValue
} from 'recoil';
import {
  tradeChartHeightState,
  tradeChartWidthState,
} from '#state/gui';
import {
  currentSymbolState,
  levelsShowedState,
  levelsPercentileState,
  levelsMinimumVolumeState,
  levelsMinimumVolumeSpotState,
  updateRateIntervalState,
  orderBookScaleState,
  enableZoomState,
} from '#state';
import {
  wsTimerState,
  timeMachineTimeState,
  candlesState,
  candlesSelector,
  clustersState,
  clustersSelector,
  tradesState,
  tradesSelector,
} from '#state/data';

import {
  balanceState,
  coinState,
  lastPriceState,
  activeOrderState
} from '#state/trades';

import {
  weightedAvgSelector,
  ema1Selector,
  ema2Selector,
  ma3Selector,
} from '#state/indicators';

import { getSecondStart } from '../../utils';

import { EMA, SMA } from '@debut/indicators';

import { tradeBuffers, orderBooks } from '#state/local';

let cacheStartTime
let featureTradePointsCache = []
let featureUpdatePointsCache = []
let featureBookPointsCache = []

let spotTradePointsCache = []
let spotUpdatePointsCache = []
let spotBookPointsCache = []

const showEmas = false
const showAggregatedTrades = true
const tradesAggregationInterval = 10
// 
const showFuturesTrades = true
const showFuturesUpdates = false
const showFuturesBook = true
// 
const showSpotTrades = false
const showSpotUpdates = false
const showSpotBook = true
// 
const minSizeFuturesTrades = 3
const minSizeSpotTrades = 3
const minSizeFuturesBook = 1
const minSizeSpotBook = 1
// 
let minVolumeKUSD = 100000
let minVolumeSpotKUSD = 100000

let currentX = 0

const D3FC = (props) => {
  const myRef = useRef();
  const {
    featureTradePoints,
    spotTradePoints,
    emaPoints1,
    emaPoints2,
    emaPoints3,
    futuresUpdates,
    futuresBook,
    spotUpdates,
    spotBook,
    scale,
    priceScale,
    keys,
    width,
    height,
    timeWindow,
    timePoints,
    startTime,
  } = props


  const setTimeMachineTime = useSetRecoilState(timeMachineTimeState);
  const zoomEnabled = useRecoilValue(enableZoomState);

  // const updateRateInterval = useRecoilValue(updateRateIntervalState);

  // console.log('spotUpdates', spotUpdates, 'spotBook', spotBook)

  const { minX, maxX, minY, maxY } = scale
  // console.log('scale', scale)

  const xwindow = maxX + 0.01
  const x = d3.scaleLinear()
    // .domain([0.8, xwindow]) //.domain([minX, maxX]); //.domain([0, 60])
    .domain([xwindow - timeWindow, xwindow]) //.domain([minX, maxX]); //.domain([0, 60])
  // .domain([0.82, 1.125])
  const y = d3.scaleLinear()
    .domain([minY * (1 - priceScale), maxY * (1 + priceScale)])//.domain([minY, maxY]) //.domain([-100, 300]) //

  // .tickArguments([5])
  // .tickCenterLabel(true);

  // *******************************************************
  // Futures Trades
  // *******************************************************
  // let extSize = fc
  //   .extentLinear()
  //   .accessors([d => d.s])(featureTradePoints)

  // let alpha = d3
  //   .scaleLinear()
  //   .range([0.5, 1])
  //   .domain(extSize);

  let fillColor = fc
    .webglFillColor()
    .value(d => {
      const a = 1 //alpha(d.s)
      return getColorFeatureTrades(a, d.m)
    })
    .data(featureTradePoints);

  // let size = d3
  //   .scaleLinear()
  //   .range([3, 300])
  //   .domain(extSize);



  const seriesFeatureTrades = fc
    .seriesWebglPoint()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    // .type(d3.symbolSquare)
    .size(d => futuresTradeSize(d, minSizeFuturesTrades))
    .decorate(program => {
      // Set the color of the points.
      fillColor(program);
      // Enable blending of transparent colors.
      const context = program.context();
      if (context) {
        context.enable(context.BLEND);
        context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);
      }
    });

  // *******************************************************
  // Spot Trades
  // *******************************************************
  // let extSizeSpotTrades = fc
  //   .extentLinear()
  //   .accessors([d => d.s])(spotTradePoints)

  // let alphaSpotTrades = d3
  //   .scaleLinear()
  //   .range([0.5, 1])
  //   .domain(extSizeSpotTrades);

  let fillColorSpotTrades = fc
    .webglFillColor()
    .value(d => {
      const a = 1 //alphaSpotTrades(d.s)
      return getColorSpotTrades(a, d.m)
    })
    .data(spotTradePoints);


  const seriesSpotTrades = fc
    .seriesWebglPoint()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    // .type(d3.symbolSquare)
    .size(d => spotTradeSize(d, minSizeSpotTrades))
    .decorate(program => {
      // Set the color of the points.
      fillColorSpotTrades(program);
      // Enable blending of transparent colors.
      const context = program.context();
      if (context) {
        context.enable(context.BLEND);
        context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);
      }
    });

  // *******************************************************
  // Futures Emas
  // *******************************************************

  const ema1 = fc
    .seriesWebglLine()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    .decorate((program, _, index) => {
      fc
        .webglStrokeColor()
        .value(() => {
          // const { r, g, b, opacity } = d3.color(color(index));
          // return [r / 255, g / 255, b / 255, opacity];
          return [255 / 256, 1 / 256, 1 / 256, 1];
        })
        .data(featureTradePoints)(program);
    });
  const ema2 = fc
    .seriesWebglLine()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    .decorate((program, _, index) => {
      fc
        .webglStrokeColor()
        .value(() => {
          return [1 / 256, 255 / 256, 1 / 256, 1];
        })
        .data(featureTradePoints)(program);
    });


  const ma3 = fc
    .seriesWebglLine()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    .decorate((program, _, index) => {
      fc
        .webglStrokeColor()
        .value(() => {
          return [255 / 256, 255 / 256, 255 / 256, 1];
        })
        .data(featureTradePoints)(program);
    });

  const gridline = fc.annotationCanvasGridline()
  gridline.xDecorate((context, _, index) => {
    context.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  });
  gridline.yDecorate((context, _, index) => {
    context.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  });

  // *******************************************************
  // Futures Updates
  // *******************************************************

  let extSizeFutures = fc
    .extentLinear()
    .accessors([d => d.s])(futuresUpdates)

  let alphaFutures = d3
    .scaleLinear()
    .range([0.09, 0.66])
    .domain(extSizeFutures);

  let fillColorFutures = fc
    .webglFillColor()
    .value(d => {
      const a = alphaFutures(d.s)
      return getColorFuturesUpdates(a, d)
    })
    .data(futuresUpdates);

  let sizeFutures = d3
    .scaleLinear()
    .range([0.5, 25])
    .domain(extSizeFutures);

  const webglSeriesFutures = fc
    .seriesWebglPoint()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    // .type(d3.symbolSquare)
    .size(d => sizeFutures(d.s))
    .decorate(program => {
      // Set the color of the points.
      fillColorFutures(program);
      // Enable blending of transparent colors.
      const context = program.context();
      if (context) {
        context.enable(context.BLEND);
        context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);
      }
    });

  // *******************************************************
  // Futures Book
  // *******************************************************
  let extSizeFuturesBook = fc
    .extentLinear()
    .accessors([d => d.s])(futuresBook)

  let fillColorFuturesBook = fc
    .webglFillColor()
    .value(d => {
      return getColorFuturesBook(1, d)
    })
    .data(futuresBook);

  let sizeFuturesBook = d3
    .scaleLinear()
    .range([0.1, 20])
    .domain(extSizeFuturesBook);

  const webglSeriesFuturesBook = fc
    .seriesWebglPoint()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    // .type(d3.symbolSquare)
    .size(d => futuresBookSize(d, minVolumeKUSD, minSizeFuturesBook)) //sizeFuturesBook(d.s))
    .decorate(program => {
      // Set the color of the points.
      fillColorFuturesBook(program);
      // Enable blending of transparent colors.
      const context = program.context();
      if (context) {
        context.enable(context.BLEND);
        context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);
      }
    });

  // *******************************************************
  // Spot Updates
  // *******************************************************

  let extSizeSpot = fc
    .extentLinear()
    .accessors([d => d.s])(spotUpdates)

  let alphaSpot = d3
    .scaleLinear()
    .range([0.09, 0.66])
    .domain(extSizeSpot);

  let fillColorSpot = fc
    .webglFillColor()
    .value(d => {
      const a = alphaSpot(d.s)
      return getColorSpotUpdates(a, d)
    })
    .data(spotUpdates);

  let sizeSpot = d3
    .scaleLinear()
    .range([0.5, 20])
    .domain(extSizeSpot);

  const webglSeriesSpot = fc
    .seriesWebglPoint()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    // .type(d3.symbolSquare)
    .size(d => sizeSpot(d.s))
    .decorate(program => {
      // Set the color of the points.
      fillColorSpot(program);
      // Enable blending of transparent colors.
      const context = program.context();
      if (context) {
        context.enable(context.BLEND);
        context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);
      }
    });

  // *******************************************************
  // Spot Book
  // *******************************************************

  let extSizeSpotBook = fc
    .extentLinear()
    .accessors([d => d.s])(spotBook)

  let fillColorSpotBook = fc
    .webglFillColor()
    .value(d => {
      return getColorSpotBook(1, d)
    })
    .data(spotBook);

  let sizeSpotBook = d3
    .scaleLinear()
    .range([0.1, 20])
    .domain(extSizeSpotBook);

  const webglSeriesSpotBook = fc
    .seriesWebglPoint()
    .crossValue(d => d.x)
    .mainValue(d => d.y)
    // .type(d3.symbolSquare)
    .size(d => spotBookSize(d, minVolumeSpotKUSD, minSizeSpotBook))//sizeSpotBook(d.s))
    .decorate(program => {
      // Set the color of the points.
      fillColorSpotBook(program);
      // Enable blending of transparent colors.
      const context = program.context();
      if (context) {
        context.enable(context.BLEND);
        context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);
      }
    });


  // *******************************************************
  // Time Points
  // *******************************************************

  const timePointsSeries = fc
    .seriesCanvasBar()
    .bandwidth((d, i) => {
      return 2
    })
    .orient('vertical')
    .crossValue(d => d.x)
    .mainValue(d => d.m)
    .baseValue(d => d.b)
    .decorate((ctx, d, index) => {
      if (d.back === 'mouse') {
        ctx.fillStyle = `rgba(255, 255, 255, ${0.5})`;
      } else if (d.back === 'openShort' || d.back === 'closeLong') {
        ctx.fillStyle = `rgba(255, 100, 100, ${0.5})`;
      } else if (d.back === 'openLong' || d.back === 'closeShort') {
        ctx.fillStyle = `rgba(100, 255, 100, ${0.5})`;
      } else if (d.back === 'start' || d.back === 'end') {
        ctx.fillStyle = `rgba(255, 255, 255, ${0.3})`;
      } else {
        ctx.fillStyle = `rgba(100, 100, 255, ${0.3})`;
      }
    });

  // *******************************************************
  // Combine Series
  // *******************************************************

  const dataContainer = {
    featureTrades: featureTradePoints,
    spotTrades: spotTradePoints,
    ema1: emaPoints1,
    ema2: emaPoints2,
    ma3: emaPoints3,
    futuresUpdates: futuresUpdates,
    futuresBook: futuresBook,
    spotUpdates: spotUpdates,
    spotBook: spotBook,
    timePoints: timePoints,
  }

  // console.log('dataContainer', dataContainer)
  const showSeries = []
  // order by z-index
  // order books
  if (showSpotBook) showSeries.push(webglSeriesSpotBook)
  if (showFuturesBook) showSeries.push(webglSeriesFuturesBook)
  // order books updates
  if (showSpotUpdates) showSeries.push(webglSeriesSpot)
  if (showFuturesUpdates) showSeries.push(webglSeriesFutures)
  // trades
  if (showSpotTrades) showSeries.push(seriesSpotTrades)
  if (showFuturesTrades) showSeries.push(seriesFeatureTrades)
  // emas
  if (showEmas) {
    showSeries.push(ma3, ema2, ema1)
  }


  let multiSeries = fc
    .seriesWebglMulti()
    .series(showSeries)
    .mapping((dc, index, series) => {
      switch (series[index]) {
        case ema1:
          return dc.ema1;
        case ema2:
          return dc.ema2;
        case ma3:
          return dc.ma3;
        // futures
        case seriesFeatureTrades:
          return dc.featureTrades;
        case webglSeriesFutures:
          return dc.futuresUpdates;
        case webglSeriesFuturesBook:
          return dc.futuresBook
        // spot
        case seriesSpotTrades:
          return dc.spotTrades;
        case webglSeriesSpot:
          return dc.spotUpdates;
        case webglSeriesSpotBook:
          return dc.spotBook;
        default:
          return null
      }
    });

  let canvasSeries = fc
    .seriesCanvasMulti()
    .series([gridline, timePointsSeries])
    .mapping((data, index, series) => {
      switch (series[index]) {
        case timePointsSeries:
          return data.timePoints;
        case gridline:
          return data.c1;
        default:
          return null
      }
    });

  const pointer = fc.pointer().on('point', event => {
    if (event.length) {
      const positionX = x.invert(event[0].x);
      currentX = positionX;
    }
  });

  let chart = fc
    .chartCartesian(x, y)
    .webglPlotArea(multiSeries)
    .canvasPlotArea(canvasSeries)

  if (zoomEnabled) {
    const zoom = fc.zoom().on('zoom', render);
    chart.decorate(sel => {
      sel.enter().call(zoom, x, y);
    })
  }

  chart.decorate(sel => {
    sel.enter().call(pointer);
  })


  function render() {
    d3.select(`#d3fcr-${keys}`)
      .datum(dataContainer)
      .call(chart);
  }

  useEffect(() => {
    reinitChart({ x, y, multiSeries, render })
  }, [featureTradePoints, emaPoints1,]); // keys

  useEffect(() => {
    console.log('tradesChart resize', width, height)
    reinitChart({ x, y, multiSeries, render, zoomEnabled })
  }, [height, width, zoomEnabled]);


  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     render()
  //   }, updateRateInterval)
  //   return () => {
  //     clearInterval(interval)
  //   }
  // }, [updateRateInterval, render]);

  return <div
    onClick={(e) => {
      const domain = x.domain()
      console.log('currentX', currentX, domain)
      const time = Math.round(startTime + (currentX - domain[0] * 2) * 60000)
      console.log('setTimeMachineTime', time)
      setTimeMachineTime(time)
    }}
    key={`d3fcr-${keys}`}
    id={`d3fcr-${keys}`}
    className='d3fc tradesChart'
    ref={myRef}
    style={{
      width: '100%',
      height,
    }}
  />;
};

function reinitChart(props) {
  let { x, y, multiSeries, render, zoomEnabled } = props

  const gridline = fc.annotationCanvasGridline()
  gridline.xDecorate((context, _, index) => {
    context.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  });
  gridline.yDecorate((context, _, index) => {
    context.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  });

  const chart = fc.chartCartesian(x, y)
    .webglPlotArea(multiSeries)
    .canvasPlotArea(gridline)

  if (zoomEnabled) {
    const zoom = fc.zoom().on('zoom', render);
    chart.decorate(sel => {
      sel.enter().call(zoom, x, y);
    })
  }

  render();
}




export const TradesChart = ({ replay, replayData, next }) => {
  // resize state
  const [resizing, setResizing] = useState(false);
  const [width, setWidth] = useRecoilState(tradeChartWidthState);
  const [height, setHeight] = useRecoilState(tradeChartHeightState);
  // real state
  const updateRateInterval = useRecoilValue(updateRateIntervalState);
  const wsTimer = useRecoilValue(wsTimerState);
  const currentSymbol = useRecoilValue(currentSymbolState);
  // const trades = useRecoilValue(tradesState);
  const levelsPercentile = useRecoilValue(levelsPercentileState);
  const minVolumeUSD = useRecoilValue(levelsMinimumVolumeState)
  const minVolumeSpotUSD = useRecoilValue(levelsMinimumVolumeSpotState);
  const priceScale = useRecoilValue(orderBookScaleState);
  const zoomEnabled = useRecoilValue(enableZoomState);
  const timeMachineTime = useRecoilValue(timeMachineTimeState);
  // console.log('trades', trades)
  minVolumeKUSD = minVolumeUSD * 1000
  minVolumeSpotKUSD = minVolumeSpotUSD * 1000
  // fix for d3fc-zoom
  const [rerender, setRerender] = useState(0)

  // useEffect(() => {
  //   // console.log('wsTimer', wsTimer)
  // }, [wsTimer])

  useEffect(() => {
    setRerender(rerender + 1)
  }, [width, height, minVolumeUSD, minVolumeSpotUSD, levelsPercentile, currentSymbol, zoomEnabled])

  useEffect(() => {
    featureTradePointsCache = []
    spotTradePointsCache = []
  }, [currentSymbol])

  useEffect(() => {
    featureUpdatePointsCache = []
    featureBookPointsCache = []
  }, [minVolumeUSD, levelsPercentile, priceScale, currentSymbol])

  useEffect(() => {
    spotUpdatePointsCache = []
    spotBookPointsCache = []
  }, [minVolumeSpotUSD, levelsPercentile, priceScale, currentSymbol])


  let minTime = Infinity;
  let maxTime = -Infinity;
  let minPrice = Infinity;
  let maxPrice = -Infinity;
  let minVolume = Infinity;
  let tradesStartTime = 0
  let startTime

  if (tradeBuffers.futures.length) {
    if (!cacheStartTime) cacheStartTime = tradeBuffers.futures[0].T
    const p = tradeBuffers.futures[0].p;
    minPrice = p;
    maxPrice = p;
    tradesStartTime = tradeBuffers.futures[0].T
    startTime = tradeBuffers.futures[0].T;
    minTime = (startTime - cacheStartTime) / 60000;
    maxTime = (Date.now() + 1000 - cacheStartTime) / 60000
    // maxTime = minTime + 2;
  }

  let timeWindow = 2
  if (replay && tradeBuffers.futures.length) {
    const start = tradeBuffers.futures[0].T
    const end = tradeBuffers.futures[tradeBuffers.futures.length - 1].T
    cacheStartTime = start
    maxTime = (end + 1000 - cacheStartTime) / 60000
    timeWindow = Math.round((end - start) / 60000)
    console.log('replay', replay, timeWindow, cacheStartTime, startTime, tradesStartTime, minTime, maxTime)
  }



  const ema1 = new EMA(8);
  const ema2 = new EMA(13);
  const ma3 = new SMA(55);

  let emaPoints1 = []
  let emaPoints2 = []
  let emaPoints3 = []

  let featureTradePoints = []
  let spotTradePoints = []
  let featureTradesBuffer = []
  let spotTradesBuffer = []
  let lastTime = 0

  // *******************************************************
  // Futures Trades
  // *******************************************************

  if (showFuturesTrades) {
    let startIndex = -1
    featureTradePointsCache.forEach((p, i) => {
      if (startIndex === -1 && p.t >= tradesStartTime) startIndex = i
      if (p.y < minPrice) minPrice = p.y
      if (p.y > maxPrice) maxPrice = p.y
    })

    // remove old trades
    const filteredTradesPoints = startIndex >= 0 ? featureTradePointsCache.slice(startIndex) : featureTradePointsCache
    featureTradePoints = filteredTradesPoints
    // 
    const cacheEndTime = featureTradePoints.length ? featureTradePoints[featureTradePoints.length - 1].t : 0
    const cacheEndId = featureTradePoints.length ? featureTradePoints[featureTradePoints.length - 1].i : 0

    const tradeUpdates = tradeBuffers.futures.filter((t) => {
      const tradeId = t.a // .t trade id for @trades, .a for @aggTrades
      return t.T >= cacheEndTime && (replay ? true : tradeId > cacheEndId)
    })


    // console.log('tradeUpdates', tradeUpdates.length)
    for (const trade of tradeUpdates) {
      try {
        const p = trade.p;
        const v = trade.q;
        let ignore = false;

        if (v < minVolume) minVolume = v;
        const ut = (trade.T - cacheStartTime) / 60000

        if (ut < minTime) minTime = ut
        else if (ut > maxTime) maxTime = ut

        if (p < minPrice && minPrice / p > 1.02) {
          // console.log('ignore top', p, maxPrice);
          ignore = true
        }
        if (p > maxPrice && p / maxPrice > 1.02) {
          // console.log('ignore bottom', p, maxPrice);
          ignore = true
        }
        if (ignore) {
          // console.log('ignore trade', p, maxPrice, ignoreTop, minPrice, ignoreBottom);
          // ignore = true;
        } else {
          if (p < minPrice) minPrice = p;
          else if (p > maxPrice) maxPrice = p;
        }

        const start = Math.floor(trade.T / tradesAggregationInterval) * tradesAggregationInterval

        if (lastTime !== start) {
          if (featureTradesBuffer.length) {
            // const lastWeightedAvg = weightedAvg(featureTradesBuffer);
            const [lastWeightedAvg, totalVolume, deltaVolume, buyVolume, sellVolume] = weightedAvg(featureTradesBuffer);
            if (showEmas) {
              // console.log('featureTradesBuffer lastWeightedAvg', lastWeightedAvg);
              // const lastEMA = ema1.nextValue(lastWeightedAvg)
              // const lastEMA2 = ema2.nextValue(lastWeightedAvg)
              const lastma3 = ma3.nextValue(lastWeightedAvg)

              // if (lastEMA) {
              //   emaPoints1.push({
              //     x: ut,
              //     y: lastEMA,
              //   })
              // }
              // if (lastEMA2) {
              //   emaPoints2.push({
              //     x: ut,
              //     y: lastEMA2,
              //   })
              // }
              if (lastma3) {
                emaPoints3.push({
                  x: ut,
                  y: lastma3,
                })
              }
            }

            if (showAggregatedTrades) {
              const lastTrade = featureTradesBuffer[featureTradesBuffer.length - 1]

              // const c = {
              //   t: lastTrade.T,
              //   i: lastTrade.t,
              //   x: ut,
              //   y: lastWeightedAvg,
              //   s: totalVolume,
              //   u: lastWeightedAvg * totalVolume,
              //   m: deltaVolume < 0 ? true : false,  
              // }

              // featureTradePoints.push(c)

              const tradeId = lastTrade.a // .t trade id for @trades, .a for @aggTrades

              const sell = {
                t: lastTrade.T,
                i: tradeId, 
                x: ut,
                y: lastWeightedAvg,
                s: sellVolume,
                u: lastWeightedAvg * sellVolume,
                m: true
              }

              const buy = {
                t: lastTrade.T,
                i: tradeId,
                x: ut,
                y: lastWeightedAvg,
                s: buyVolume,
                u: lastWeightedAvg * buyVolume,
                m: false
              }

              if (deltaVolume >= 0) {
                featureTradePoints.push(buy)
                featureTradePoints.push(sell)
              } else {
                featureTradePoints.push(sell)
                featureTradePoints.push(buy)
              }
            }

            featureTradesBuffer = []
          }
          lastTime = start
          // currentIndex++
        } else {
          featureTradesBuffer.push(trade)
          lastTime = start
          // console.log('featureTradesBuffer push', featureTradesBuffer);
        }

        if (!ignore && !showAggregatedTrades) {
          // group trades
          const tradeId = trade.t // .t trade id for @trades, .a for @aggTrades

          const prevPoint = featureTradePoints.length ? featureTradePoints[featureTradePoints.length - 1] : null
          if (prevPoint && trade.T - prevPoint.t < 100 && trade.p === prevPoint.y && trade.m === prevPoint.m) {
            // console.log('group trades', trade.T, prevPoint.t, trade.p, prevPoint.y, trade.m, prevPoint.m);

            prevPoint.s += v
            prevPoint.i = tradeId
            prevPoint.x = ut

          }
          else {
            const c = {
              t: trade.T,
              i: tradeId,
              x: ut,
              y: p,
              s: v,
              u: p * v,
              m: trade.m
            }

            featureTradePoints.push(c)
          }
        }


      } catch (error) {
        console.log('error', error);
      }

    }
  }

  // *******************************************************
  // Spot Trades
  // *******************************************************

  if (showSpotTrades) {
    let startIndex = -1
    spotTradePointsCache.forEach((p, i) => {
      if (startIndex === -1 && p.t >= tradesStartTime) startIndex = i
      if (p.y < minPrice) minPrice = p.y
      if (p.y > maxPrice) maxPrice = p.y
    })

    // remove old trades
    const filteredTradesPoints = startIndex >= 0 ? spotTradePointsCache.slice(startIndex) : spotTradePointsCache
    spotTradePoints = filteredTradesPoints
    // 
    const cacheEndTime = spotTradePoints.length ? spotTradePoints[spotTradePoints.length - 1].t : 0
    const cacheEndId = spotTradePoints.length ? spotTradePoints[spotTradePoints.length - 1].i : 0

    const tradeUpdates = tradeBuffers.spot.filter((t) => t.T >= cacheEndTime && (replay ? true : t.t > cacheEndId))

    for (const trade of tradeUpdates) {
      try {
        const p = trade.p;
        const v = trade.q;
        let ignore = false;

        if (v < minVolume) minVolume = v;
        const ut = (trade.T - cacheStartTime) / 60000

        // if (ut < minTime) minTime = ut
        // else if (ut > maxTime) maxTime = ut

        if (p < minPrice && minPrice / p > 1.01) {
          console.log('ignore top', p, maxPrice);
          ignore = true
        }
        if (p > maxPrice && p / maxPrice > 1.01) {
          console.log('ignore bottom', p, maxPrice);
          ignore = true
        }

        if (ignore) {
          // console.log('ignore trade', p, minPrice, maxPrice);
          // ignore = true;
        } else {
          if (p < minPrice) minPrice = p;
          else if (p > maxPrice) maxPrice = p;
        }

        const start = Math.floor(trade.T / tradesAggregationInterval) * tradesAggregationInterval

        if (lastTime !== start) {
          if (spotTradesBuffer.length) {
            // const lastWeightedAvg = weightedAvg(spotTradesBuffer);
            const [lastWeightedAvg, totalVolume, deltaVolume, buyVolume, sellVolume] = weightedAvg(spotTradesBuffer);

            if (showEmas) {
              // console.log('spotTradesBuffer lastWeightedAvg', lastWeightedAvg);
              // const lastEMA = ema1.nextValue(lastWeightedAvg)
              // const lastEMA2 = ema2.nextValue(lastWeightedAvg)
              const lastma3 = ma3.nextValue(lastWeightedAvg)

              // if (lastEMA) {
              //   emaPoints1.push({
              //     x: ut,
              //     y: lastEMA,
              //   })
              // }
              // if (lastEMA2) {
              //   emaPoints2.push({
              //     x: ut,
              //     y: lastEMA2,
              //   })
              // }
              if (lastma3) {
                emaPoints3.push({
                  x: ut,
                  y: lastma3,
                })
              }
            }

            if (showAggregatedTrades) {
              const lastTrade = spotTradesBuffer[spotTradesBuffer.length - 1]

              // const combined = {
              //   t: lastTrade.T,
              //   i: lastTrade.t,
              //   x: ut,
              //   y: lastWeightedAvg,
              //   s: totalVolume,
              //   u: lastWeightedAvg * totalVolume,
              //   m: deltaVolume < 0 ? true : false,  
              // }
              // spotTradePoints.push(combined)

              const sell = {
                t: lastTrade.T,
                i: lastTrade.t,
                x: ut,
                y: lastWeightedAvg,
                s: sellVolume,
                u: lastWeightedAvg * sellVolume,
                m: true
              }

              const buy = {
                t: lastTrade.T,
                i: lastTrade.t,
                x: ut,
                y: lastWeightedAvg,
                s: buyVolume,
                u: lastWeightedAvg * buyVolume,
                m: false
              }

              if (deltaVolume >= 0) {
                spotTradePoints.push(buy)
                spotTradePoints.push(sell)
              } else {
                spotTradePoints.push(sell)
                spotTradePoints.push(buy)
              }
            }

            spotTradesBuffer = []
          }
          lastTime = start
          // currentIndex++
        } else {
          spotTradesBuffer.push(trade)
          lastTime = start
          // console.log('spotTradesBuffer push', spotTradesBuffer);
        }

        if (!ignore && !showAggregatedTrades) {
          // group trades
          const prevPoint = spotTradePoints.length ? spotTradePoints[spotTradePoints.length - 1] : null
          if (prevPoint && trade.T - prevPoint.t < 100 && trade.p === prevPoint.y && trade.m === prevPoint.m) {
            // console.log('group trades', trade.T, prevPoint.t, trade.p, prevPoint.y, trade.m, prevPoint.m);
            prevPoint.s += v
            prevPoint.i = trade.t
            prevPoint.x = ut

          }
          else {
            const c = {
              t: trade.T,
              i: trade.t,
              x: ut,
              y: p,
              s: v,
              u: p * v,
              m: trade.m
            }

            spotTradePoints.push(c)
          }
        }


      } catch (error) {
        console.log('error', error);
      }

    }
  }


  // *******************************************************
  // Futures Order Book Updates
  // *******************************************************
  let futuresUpdates = []
  if (showFuturesUpdates) {
    futuresUpdates = handleFuturesUpdates(tradesStartTime, minVolumeKUSD, priceScale, minPrice, maxPrice)
  }

  // *******************************************************
  // Futures Order Book
  // *******************************************************
  let futuresBook = []
  if (showFuturesBook) {
    futuresBook = handleFuturesBook(tradesStartTime, minVolumeKUSD, priceScale, minPrice, maxPrice, levelsPercentile)
  }

  // *******************************************************
  // Spot Order Book Updates
  // *******************************************************
  let spotUpdates = []
  if (showSpotUpdates) {
    spotUpdates = handleSpotUpdates(tradesStartTime, minVolumeSpotKUSD, priceScale, minPrice, maxPrice)
  }

  // *******************************************************
  // Futures Order Book
  // *******************************************************
  let spotBook = []
  if (showSpotBook) {
    spotBook = handleSpotBook(tradesStartTime, minVolumeSpotKUSD, priceScale, minPrice, maxPrice, levelsPercentile)
  }


  // *******************************************************
  // Other
  // *******************************************************


  const scale = {
    minX: minTime,
    maxX: maxTime,
    minY: minPrice,
    maxY: maxPrice,
  }

  // console.log('tradeBuffers', tradeBuffers);
  // console.log('orderBooks.futures', orderBooks.futures);
  // console.log('orderBooks.spot', orderBooks.spot);

  // console.log('futuresUpdates', futuresUpdates);
  // console.log('futuresBook', futuresBook);

  // console.log('featureTradePoints', featureTradePoints);
  // console.log('spotTradePoints', spotTradePoints);

  // console.log('emaPoints1', emaPoints1);

  featureTradePointsCache = featureTradePoints
  featureUpdatePointsCache = futuresUpdates
  featureBookPointsCache = futuresBook

  spotTradePointsCache = spotTradePoints
  spotUpdatePointsCache = spotUpdates
  spotBookPointsCache = spotBook

  // console.log('spotTradePoints', spotTradePoints);

  if (!featureTradePoints.length) return null

  const totalPoints = featureTradePoints.length + futuresUpdates.length + futuresBook.length + spotTradePoints.length + spotUpdates.length + spotBook.length

  const timePoints = []
  if (replay && replayData) {
    const shiftLeft = 0

    if (replayData?.event?.history?.length) {
      for (const ev of replayData.event.history) {
        const eventTime = ev.time
        if (eventTime) {
          const pointTime = (eventTime - startTime) / 60000;
          timePoints.push({
            x: pointTime - shiftLeft,
            b: minPrice * (1 - priceScale),
            m: maxPrice * (1 + priceScale),
            v: 1,
            back: ev.type || 'replay'
          })
        }
      }
    } else {
      const rt = replayData?.event?.now || replayData?.time
      if (rt) {
        const replayTime = rt
        const pointTime = (replayTime - startTime) / 60000;

        timePoints.push({
          x: pointTime - shiftLeft,
          b: minPrice * (1 - priceScale),
          m: maxPrice * (1 + priceScale),
          v: 1,
          back: 'replay'
        })
      }
    }
    if (currentX) {
      timePoints.push({
        x: (timeMachineTime - startTime) / 60000,
        b: minPrice * (1 - priceScale),
        m: maxPrice * (1 + priceScale),
        v: 1,
        back: 'mouse'
      })
    }
  }

  return (<>
    <Resizable
      minHeight={200}
      minWidth={100}
      onResizeStop={(e, direction, ref, d) => {
        const newWidth = width + d.width
        const newHeight = height + d.height
        // setWidth(newWidth);
        setHeight(newHeight);
        setResizing(false);
        // localforage.setItem('tradeChartWidthState', newWidth);
        localforage.setItem('tradeChartHeightState', newHeight);
      }}
      onResizeStart={(e, direction, ref, d) => {
        setResizing(true);
      }}
      style={{
        boxShadow: '1px 1px 2px 1px rgba(0,0,0,0.5)',
        backgroundColor: resizing ? 'black' : 'transparent',
      }}
    >
      {!replay && totalPoints / updateRateInterval > 1000 ? <div>
        Points:
        Total: {totalPoints + ' '}
        | FTrades: {featureTradePointsCache.length + ' '}
        | FUpdates: {featureUpdatePointsCache.length + ' '}
        | FBook: {featureBookPointsCache.length + ' '}
        | STrades: {spotTradePointsCache.length + ' '}
        | SUpdates: {spotUpdatePointsCache.length + ' '}
        | SBook: {spotBookPointsCache.length + ' '}
      </div> : null}

      <D3FC
        keys={`r-${rerender}`}
        className='d3fc'
        resizing={resizing}
        width={width}
        height={height}
        featureTradePoints={featureTradePoints}
        spotTradePoints={spotTradePoints}
        futuresUpdates={futuresUpdates}
        futuresBook={futuresBook}
        spotUpdates={spotUpdates}
        spotBook={spotBook}
        emaPoints1={emaPoints1}
        emaPoints2={emaPoints2}
        emaPoints3={emaPoints3}
        scale={scale}
        priceScale={priceScale}
        timeWindow={timeWindow}
        timePoints={timePoints}
        startTime={startTime}
      />
    </Resizable>

  </>);
};


function getColorFeatureTrades(a, sell) {
  if (sell) { // bug?
    return [217 / 256, 67 / 256, 8 / 256, a]
  } else {
    return [8 / 256, 157 / 256, 217 / 256, a]
  }
}
function getColorSpotTrades(a, sell) {
  if (sell) {
    return [123 / 256, 9 / 256, 216 / 256, a]
  } else {
    return [130 / 256, 216 / 256, 9 / 256, a]
  }
}

function getColorFuturesUpdates(a, d) {
  if (d.a) {
    return [7 / 256, 117 / 256, 218 / 256, a]
  } else {
    return [218 / 256, 108 / 256, 7 / 256, a]
  }

}
function getColorSpotUpdates(a, d) {
  if (d.a) {
    return [62 / 256, 218 / 256, 7 / 256, a]
  } else {
    return [215 / 256, 7 / 256, 218 / 256, a]
  }

}

// minVolumeKUSD = minVolumeUSD * 1000
// minVolumeSpotKUSD = minVolumeSpotUSD * 1000

function getColorFuturesBook(a, d) {
  const dv = d.u / minVolumeKUSD
  if (d.a) {
    if (dv < 2) return [218 / 256, 108 / 256, 7 / 256, 0.2]
    if (dv < 4) return [218 / 256, 108 / 256, 7 / 256, 0.4]
    if (dv < 8) return [218 / 256, 108 / 256, 7 / 256, 0.75]
    return [218 / 256, 108 / 256, 7 / 256, 1]
    // return [218 / 256, 108 / 256, 7 / 256, a]
  } else {
    if (dv < 2) return [7 / 256, 117 / 256, 218 / 256, 0.2]
    if (dv < 4) return [7 / 256, 117 / 256, 218 / 256, 0.4]
    if (dv < 8) return [7 / 256, 117 / 256, 218 / 256, 0.75]
    return [7 / 256, 117 / 256, 218 / 256, 1]
    // return [7 / 256, 117 / 256, 218 / 256, a]
  }
  // if (d.a) {
  //   return [218 / 256, 108 / 256, 7 / 256, a]
  // } else {
  //   return [7 / 256, 117 / 256, 218 / 256, a]
  // }
}
function getColorSpotBook(a, d) {
  const dv = d.u / minVolumeSpotKUSD
  if (d.a) {
    if (dv < 2) return [215 / 256, 7 / 256, 218 / 256, 0.2]
    if (dv < 4) return [215 / 256, 7 / 256, 218 / 256, 0.4]
    if (dv < 8) return [215 / 256, 7 / 256, 218 / 256, 0.75]
    return [215 / 256, 7 / 256, 218 / 256, 1]
    // return [215 / 256, 7 / 256, 218 / 256, a]
  } else {
    if (dv < 2) return [62 / 256, 218 / 256, 7 / 256, 0.2]
    if (dv < 4) return [62 / 256, 218 / 256, 7 / 256, 0.4]
    if (dv < 8) return [62 / 256, 218 / 256, 7 / 256, 0.75]
    return [62 / 256, 218 / 256, 7 / 256, 1]
    // return [62 / 256, 218 / 256, 7 / 256, a]
  }
  // if (d.a) {
  //   return [215 / 256, 7 / 256, 218 / 256, a]
  // } else {
  //   return [62 / 256, 218 / 256, 7 / 256, a]
  // }
}

function futuresBookSize(d, filteredSize, minSize) {
  // return size(d.s)
  if (d.u < 1 * filteredSize) return 1 * minSize
  if (d.u < 2 * filteredSize) return 2 * minSize
  if (d.u < 4 * filteredSize) return 4 * minSize
  if (d.u < 8 * filteredSize) return 8 * minSize
  if (d.u < 16 * filteredSize) return 16 * minSize
  if (d.u < 32 * filteredSize) return 24 * minSize
  if (d.u < 64 * filteredSize) return 32 * minSize
  if (d.u < 128 * filteredSize) return 40 * minSize
  if (d.u < 256 * filteredSize) return 48 * minSize
  if (d.u < 512 * filteredSize) return 56 * minSize
  if (d.u < 1024 * filteredSize) return 64 * minSize
  if (d.u < 2048 * filteredSize) return 72 * minSize
  if (d.u < 4096 * filteredSize) return 80 * minSize
  if (d.u < 8192 * filteredSize) return 88 * minSize
  if (d.u < 16384 * filteredSize) return 96 * minSize
  return 104 * minSize
}

function spotBookSize(d, filteredSize, minSize) {
  // return size(d.s)
  if (d.u < 1 * filteredSize) return 1 * minSize
  if (d.u < 2 * filteredSize) return 2 * minSize
  if (d.u < 4 * filteredSize) return 4 * minSize
  if (d.u < 8 * filteredSize) return 8 * minSize
  if (d.u < 16 * filteredSize) return 16 * minSize
  if (d.u < 32 * filteredSize) return 24 * minSize
  if (d.u < 64 * filteredSize) return 32 * minSize
  if (d.u < 128 * filteredSize) return 40 * minSize
  if (d.u < 256 * filteredSize) return 48 * minSize
  if (d.u < 512 * filteredSize) return 56 * minSize
  if (d.u < 1024 * filteredSize) return 64 * minSize
  if (d.u < 2048 * filteredSize) return 72 * minSize
  if (d.u < 4096 * filteredSize) return 80 * minSize
  if (d.u < 8192 * filteredSize) return 88 * minSize
  if (d.u < 16384 * filteredSize) return 96 * minSize
  return 104 * minSize
}

function futuresTradeSize(d, minSize) {
  // return size(d.s)
  if (d.u < 1000) return 1 * minSize
  if (d.u < 2000) return 2 * minSize
  if (d.u < 4000) return 4 * minSize
  if (d.u < 8000) return 8 * minSize
  if (d.u < 16000) return 16 * minSize
  if (d.u < 32000) return 24 * minSize
  if (d.u < 64000) return 32 * minSize
  if (d.u < 128000) return 40 * minSize
  if (d.u < 256000) return 48 * minSize
  if (d.u < 512000) return 56 * minSize
  if (d.u < 1024000) return 64 * minSize
  if (d.u < 2048000) return 72 * minSize
  if (d.u < 4096000) return 80 * minSize
  return 100 * minSize
}

function spotTradeSize(d, minSize) {
  if (d.u < 1000) return 1 * minSize
  if (d.u < 2000) return 2 * minSize
  if (d.u < 4000) return 4 * minSize
  if (d.u < 8000) return 8 * minSize
  if (d.u < 16000) return 16 * minSize
  if (d.u < 32000) return 24 * minSize
  if (d.u < 64000) return 32 * minSize
  if (d.u < 128000) return 40 * minSize
  if (d.u < 256000) return 48 * minSize
  if (d.u < 512000) return 56 * minSize
  if (d.u < 1024000) return 64 * minSize
  if (d.u < 2048000) return 72 * minSize
  if (d.u < 4096000) return 80 * minSize
  return 100 * minSize
}

function getColorFutures2(a, d) {
  const minAlpha = 0.1
  const alpha = Math.max(minAlpha, a)
  if (a > 0.8)
    return [alpha * 0.7, alpha * 0.1, alpha * 0.99, alpha]
  else if (a <= 0.8 && a > 0.6)
    return [alpha * 0.8, alpha * 0.1, alpha * 0.1, alpha]
  else if (a <= 0.6 && a > 0.3)
    return [alpha * 0.01, alpha * 0.5, alpha * 0.01, alpha]
  else if (a <= 0.3 && a > 0.05)
    return [alpha * 0.3, alpha * 0.5, alpha * 0.8, alpha]
  else if (a <= 0.05)
    return [alpha * 0.1, alpha * 0.3, alpha * 0.9, alpha]
}

const weightedAvg = (trades) => {
  let sum = 0, totalVolume = 0, deltaVolume = 0, buyVolume = 0, sellVolume = 0;

  trades.forEach(trade => {
    const q = trade.q
    sum += trade.p * q
    totalVolume += q;
    if (trade.m) {
      deltaVolume -= q
      sellVolume += q
    } else {
      deltaVolume += q
      buyVolume += q
    }
  });
  const result = sum / totalVolume;
  return [result, totalVolume, deltaVolume, buyVolume, sellVolume];
};

// *******************************************************
// Geeting Data Points
// *******************************************************

function handleTrades(props) {
  let {
    tradesStartTime,
    trades,
    featureTradePoints,
    minPrice,
    maxPrice,
    minVolume,
    minTime,
    maxTime,
    lastTime,
    featureTradesBuffer,
    showEmas,
    ma3,
    emaPoints3
  } = props

}

function handleFuturesUpdates(tradesStartTime, minVolumeUSD, priceScale, minPrice, maxPrice) {
  const upLimit = maxPrice * (1 + priceScale)
  const downLimit = minPrice * (1 - priceScale)

  let futuresUpdates = []
  let startIndex = -1
  featureUpdatePointsCache.forEach((p, i) => {
    if (startIndex === -1 && p.t >= tradesStartTime) startIndex = i
  })

  const filteredFeatureUpdatePoints = startIndex >= 0 ? featureUpdatePointsCache.slice(startIndex) : featureUpdatePointsCache
  futuresUpdates = filteredFeatureUpdatePoints

  const selectedUpdates = orderBooks.futures.updatesResampled //orderBooks.futures.updates

  const cacheEndTime = futuresUpdates.length ? futuresUpdates[filteredFeatureUpdatePoints.length - 1].t : 0
  const updates = selectedUpdates.filter((u) => u.T > cacheEndTime)

  updates.forEach(update => {
    const ut = (update.T - cacheStartTime) / 60000

    update.a.forEach(([price, quantity]) => {
      const q = parseFloat(quantity);
      const p = parseFloat(price);
      const totalUSD = q * p
      if (q > 0 && p <= upLimit && totalUSD > minVolumeUSD) {
        const u = {
          t: update.T,
          x: ut,
          y: p,
          s: q,
          a: true,
        }
        futuresUpdates.push(u)
      }
    });

    update.b.forEach(([price, quantity]) => {
      const q = parseFloat(quantity);
      const p = parseFloat(price);
      const totalUSD = q * p
      if (q > 0 && p >= downLimit && totalUSD > minVolumeUSD) {
        const u = {
          t: update.T,
          x: ut,
          y: p,
          s: q,
          b: true,
        }
        futuresUpdates.push(u)
      }
    });

  });

  return futuresUpdates
}

function handleFuturesBook(tradesStartTime, minVolumeUSD, priceScale, minPrice, maxPrice, levelsPercentile) {
  let futuresBook = []

  const upLimit = maxPrice * (1 + priceScale)
  const downLimit = minPrice * (1 - priceScale)

  let startIndex = -1
  featureBookPointsCache.forEach((p, i) => {
    if (startIndex === -1 && p.t >= tradesStartTime) startIndex = i
  })

  const filteredFeatureBookPoints = startIndex >= 0 ? featureBookPointsCache.slice(startIndex) : featureBookPointsCache
  futuresBook = filteredFeatureBookPoints

  const cacheEndTime = futuresBook.length ? futuresBook[filteredFeatureBookPoints.length - 1].t : 0
  const updates = orderBooks.futures.books.filter((b) => b.lastUpdateTime > cacheEndTime)

  // console.log('handleFuturesBook', startIndex, cacheEndTime, futuresBook, updates);
  const currentBook = orderBooks.futures.book
  // const currentMetrix = calculateMetrics(currentBook.bids, currentBook.asks, levelsPercentile, upLimit, downLimit)

  updates.forEach((book, i) => {
    const ut = (book.lastUpdateTime - cacheStartTime) / 60000

    // const metrix = calculateMetrics(book.bids, book.asks, levelsPercentile, upLimit, downLimit)
    // console.log('metrix', metrix)

    let filteredAsks = 0
    book.asks.forEach((quantity, price) => {
      const totalUSD = quantity * price
      //  && quantity >= currentMetrix.asks.percentile
      //  //} && quantity >= metrix.asks.percentile) {
      if (price <= upLimit && totalUSD > minVolumeUSD) {
        filteredAsks++
        const point = {
          t: book.lastUpdateTime,
          x: ut,
          y: price,
          s: quantity,
          u: totalUSD,
          a: true,
        }
        futuresBook.push(point)
      }
    });
    // console.log('book.asks', book.asks.size, filteredAsks)

    let filteredBids = 0
    book.bids.forEach((quantity, price) => {
      const totalUSD = quantity * price
      //  && quantity >= currentMetrix.bids.percentile
      if (price >= downLimit && totalUSD > minVolumeUSD) { //} && quantity >= metrix.bids.percentile) {
        filteredBids++
        const point = {
          t: book.lastUpdateTime,
          x: ut,
          y: price,
          s: quantity,
          u: totalUSD,
          b: true,
        }
        futuresBook.push(point)
      }
    });

    const totalPoints = book.bids.size + book.asks.size
    const filteredPoints = filteredBids + filteredAsks
    const filteredPercent = filteredPoints / totalPoints
    // console.log('FuturesBook filtered', filteredPercent.toPrecision(3), totalPoints, filteredPoints, )
    // console.log('book', book)

  })

  return futuresBook
}

function handleSpotUpdates(tradesStartTime, minVolumeUSD, priceScale, minPrice, maxPrice) {
  const upLimit = maxPrice * (1 + priceScale)
  const downLimit = minPrice * (1 - priceScale)

  let spotUpdates = []
  let startIndex = -1
  spotUpdatePointsCache.forEach((p, i) => {
    if (startIndex === -1 && p.t >= tradesStartTime) startIndex = i
  })

  const filteredUpdatePoints = startIndex >= 0 ? spotUpdatePointsCache.slice(startIndex) : spotUpdatePointsCache
  spotUpdates = filteredUpdatePoints

  const selectedUpdates = orderBooks.spot.updatesResampled //orderBooks.spot.updates

  const cacheEndTime = spotUpdates.length ? spotUpdates[filteredUpdatePoints.length - 1].t : 0
  const updates = selectedUpdates.filter((u) => u.E > cacheEndTime)

  updates.forEach(update => {
    const ut = (update.E - cacheStartTime) / 60000

    update.a.forEach(([price, quantity]) => {
      const q = parseFloat(quantity);
      const p = parseFloat(price);
      const totalUSD = q * p
      if (q > 0 && p <= upLimit && totalUSD > minVolumeUSD) {
        const u = {
          t: update.E,
          x: ut,
          y: p,
          s: q,
          a: true,
        }
        spotUpdates.push(u)
      }
    });

    update.b.forEach(([price, quantity]) => {
      const q = parseFloat(quantity);
      const p = parseFloat(price);
      const totalUSD = q * p
      if (q > 0 && p >= downLimit && totalUSD > minVolumeUSD) {
        const u = {
          t: update.E,
          x: ut,
          y: p,
          s: q,
          b: true,
        }
        spotUpdates.push(u)
      }
    });

  });

  return spotUpdates
}

function handleSpotBook(tradesStartTime, minVolumeUSD, priceScale, minPrice, maxPrice, levelsPercentile) {
  let spotBook = []
  const upLimit = maxPrice * (1 + priceScale)
  const downLimit = minPrice * (1 - priceScale)

  let startIndex = -1
  spotBookPointsCache.forEach((p, i) => {
    if (startIndex === -1 && p.t >= tradesStartTime) startIndex = i
  })

  const filteredBookPoints = startIndex >= 0 ? spotBookPointsCache.slice(startIndex) : spotBookPointsCache
  spotBook = filteredBookPoints

  const cacheEndTime = spotBook.length ? spotBook[filteredBookPoints.length - 1].t : 0
  const updates = orderBooks.spot.books.filter((b) => b.lastUpdateTime > cacheEndTime)

  // console.log('handleSpotBook', startIndex, cacheEndTime, spotBook, updates);
  const currentBook = orderBooks.spot.book
  // const currentMetrix = calculateMetrics(currentBook.bids, currentBook.asks, levelsPercentile, upLimit, downLimit)

  updates.forEach((book, i) => {
    const ut = (book.lastUpdateTime - cacheStartTime) / 60000

    // const metrix = calculateMetrics(book.bids, book.asks, levelsPercentile, upLimit, downLimit)
    // console.log('metrix', metrix)

    let filteredAsks = 0
    book.asks.forEach((quantity, price) => {
      const totalUSD = quantity * price
      //  && quantity >= currentMetrix.asks.percentile
      if (price <= upLimit && totalUSD > minVolumeUSD) {// && quantity >= metrix.asks.percentile) {
        filteredAsks++
        const point = {
          t: book.lastUpdateTime,
          x: ut,
          y: price,
          s: quantity,
          u: totalUSD,
          a: true,
        }
        spotBook.push(point)
      }
    });
    // console.log('book.asks', book.asks.size, filteredAsks)

    let filteredBids = 0
    book.bids.forEach((quantity, price) => {
      const totalUSD = quantity * price
      // && quantity >= currentMetrix.bids.percentile
      if (price >= downLimit && totalUSD > minVolumeUSD) {//} && quantity >= metrix.bids.percentile) {
        filteredBids++
        const point = {
          t: book.lastUpdateTime,
          x: ut,
          y: price,
          s: quantity,
          u: totalUSD,
          b: true,
        }
        spotBook.push(point)
      }
    });

    const totalPoints = book.bids.size + book.asks.size
    const filteredPoints = filteredBids + filteredAsks
    const filteredPercent = filteredPoints / totalPoints
    // console.log('SpotBook filtered', filteredPercent.toPrecision(3), totalPoints, filteredPoints, )

  })

  return spotBook
}

function calculateMetrics(bids, asks, percentileRank, upLimit, downLimit) {
  const asksValues = Array.from(asks)
    .filter(([price, volume]) => price > downLimit)
    .map(([price, volume]) => volume);

  const bidsValues = Array.from(bids)
    .filter(([price, volume]) => price < upLimit)
    .map(([price, volume]) => volume);

  const asksPercentile = percentile(asksValues, percentileRank);
  const bidsPercentile = percentile(bidsValues, percentileRank);

  const filteredBids = new Map([...bids].filter(([price, volume]) => volume > bidsPercentile));
  const filteredAsks = new Map([...asks].filter(([price, volume]) => volume > asksPercentile));

  return {
    bids: {
      percentile: bidsPercentile,
      filtered: filteredBids,
    },
    asks: {
      percentile: asksPercentile,
      filtered: filteredAsks,
    },
  };
}


function percentile(values, p) {
  values.sort((a, b) => a - b);
  const index = (p / 100) * (values.length - 1);

  if (Math.floor(index) === index) {
    return values[index];
  } else {
    const i = Math.floor(index);
    return values[i] + (values[i + 1] - values[i]) * (index - i);
  }
}

function mean(values) {
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
}

function median(values) {
  values.sort((a, b) => a - b);
  const mid = Math.floor(values.length / 2);

  return values.length % 2 !== 0 ?
    values[mid] :
    (values[mid - 1] + values[mid]) / 2;
}